import type { NOTIFICATION_CATEGORY } from "@nlc/shared-types";

import { getCategoriesForRole, NOTIFICATION_CHANNEL } from "@nlc/shared-types";
import { twMerge } from "tailwind-merge";

import { Collapse } from "@/components/collapse";
import { Switch } from "@/components/switch";
import { useUser } from "@/hooks/useUser";

import { useSetNotificationPreference } from "../../hooks/useSetNotificationPreference";
import { categoryNameMapper } from "../utils/categoryNameMapper";
import { showToast, ToastTypeEnum } from "../utils/showToast";
import { SlackConfig } from "./SlackConfig";
import { SMSConfig } from "./SMSConfig";
import WebPushConfig from "./WebpushConfig";
import { WhatsAppConfig } from "./WhatsAppConfig";

interface ChannelPreferencesProps {
  channel: NOTIFICATION_CHANNEL;
  enabledCategories: NOTIFICATION_CATEGORY[];
  Icon: React.ElementType;
  isActive: boolean;
  isDisabled: boolean;
  label: string;
}

export function ChannelPreferences({
  channel,
  enabledCategories,
  Icon,
  isActive,
  isDisabled,
  label,
}: ChannelPreferencesProps) {
  const { mutateAsync: setTogglePreference } = useSetNotificationPreference();
  const { data: userData } = useUser();

  if (!userData) return null;

  const categories = getCategoriesForRole(userData.user.roleId);

  const configChannel: Partial<Record<NOTIFICATION_CHANNEL, JSX.Element>> = {
    [NOTIFICATION_CHANNEL.SLACK]: <SlackConfig isDisabled={isDisabled} />,
    [NOTIFICATION_CHANNEL.SMS]: <SMSConfig isDisabled={isDisabled} />,
    [NOTIFICATION_CHANNEL.WEBPUSH]: (
      <WebPushConfig isActive={isActive} isDisabled={isDisabled} />
    ),
    [NOTIFICATION_CHANNEL.WHATSAPP]: <WhatsAppConfig />,
  };

  const handleToggle = (category: NOTIFICATION_CATEGORY, enabled: boolean) => {
    setTogglePreference({ category, channel, enabled }).then(() =>
      showToast("Preference saved successfully", ToastTypeEnum.SUCCESS),
    );
  };

  return (
    <div className="w-auto lg:w-full mt-2 space-y-4">
      <Collapse
        className="h-15"
        disabled={!isActive || isDisabled}
        endAdorment={
          isActive &&
          channel !== NOTIFICATION_CHANNEL.SMS &&
          channel !== NOTIFICATION_CHANNEL.WEBPUSH &&
          channel !== NOTIFICATION_CHANNEL.SLACK
            ? undefined
            : configChannel[channel]
        }
        label={
          <div className="flex">
            <Icon
              className={twMerge(
                "mr-5",
                isActive && !isDisabled
                  ? "text-text-quaternary"
                  : "text-gray-400",
              )}
            />
            <span
              className={`${isActive && !isDisabled ? "text-button-secondary-fg" : "text-text-quaternary"}`}
            >
              {label}
            </span>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-text-quaternary font-bold">Notify when</p>
          {categories.map((category) => (
            <div className="flex" key={category}>
              <Switch
                isSelected={enabledCategories.includes(category)}
                onChange={() =>
                  handleToggle(category, !enabledCategories.includes(category))
                }
              />
              <p className="pl-4 text-text-quaternary font-medium">
                {categoryNameMapper[category]}
              </p>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
}
